import { __assign } from "tslib";
import { selectors } from '../../Stream';
import { getDataEntities } from '../../redux/entities/selectors';
import { reduxKey, streamId } from './constants';
function getStoreSlice(state) {
    return (state[reduxKey] || {
        isNewCollectionSaving: false,
        deviations: {},
    });
}
function getUserCollectionEntities(state) {
    var collections = getDataEntities(state).collections;
    if (!collections) {
        return {};
    }
    return collections;
}
function getCollectionsRaw(state) {
    var collectionIds = selectors.getItems(state, streamId) || [];
    if (!collectionIds.length) {
        return [];
    }
    return getCollectionsByFolderId(state, collectionIds);
}
function getCollectionsByFolderId(state, folderIds) {
    var collections = getUserCollectionEntities(state);
    return folderIds
        .filter(function (id) { return collections.hasOwnProperty(id); })
        .map(function (collectionId) { return collections[collectionId]; });
}
function getChangeType(added, removed, kept) {
    if (!added) {
        if (!removed) {
            return 'none';
        }
        if (!kept) {
            return 'removeAll';
        }
        if (removed === 1) {
            return 'removeOne';
        }
        return 'removeSome';
    }
    if (removed) {
        return 'misc';
    }
    if (added > 1) {
        return 'addMany';
    }
    return 'addOne';
}
function getDeviationState(state, deviationId) {
    var slice = getStoreSlice(state);
    return (slice.deviations[deviationId] || {
        isFavourite: false,
        isLoading: false,
        faveCount: 0,
        savedCollections: [],
        selectedCollections: [],
        isInitializedForCollecting: false,
    });
}
export function getCollection(state, folderId) {
    return getUserCollectionEntities(state)[folderId];
}
export function getUserCollections(state, deviationId) {
    var slice = getDeviationState(state, deviationId);
    var collections = slice
        ? getCollectionsRaw(state).map(function (collection) { return (__assign(__assign({}, collection), { isSelected: slice.selectedCollections.includes(collection.folderId) })); })
        : [];
    var streamInfo = {
        streamId: streamId,
        isInitialized: slice.isInitializedForCollecting,
        hasMore: selectors.hasMore(state, streamId),
        isFetching: selectors.isFetching(state, streamId),
        errorMsg: selectors.getErrorMsg(state, streamId),
        collections: collections,
    };
    return streamInfo;
}
export function getUserCollectionsHasMore(state, deviationId) {
    var _a;
    return !!((_a = getUserCollections(state, deviationId)) === null || _a === void 0 ? void 0 : _a.hasMore);
}
export function getUserCollectionsCount(state, deviationId) {
    var _a, _b, _c;
    return (_c = (_b = (_a = getUserCollections(state, deviationId)) === null || _a === void 0 ? void 0 : _a.collections) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
}
export function getIsNewCollectionSaving(state) {
    return getStoreSlice(state).isNewCollectionSaving;
}
export function getUpdatedEntities(state, deviationId) {
    var entities = {
        collections: {},
    };
    var deviationState = getDeviationState(state, deviationId);
    var currentFolders = deviationState.savedCollections;
    var selectedFolders = deviationState.selectedCollections;
    var addedFolders = selectedFolders.filter(function (id) { return !currentFolders.includes(id); });
    var removedFolders = currentFolders.filter(function (id) { return !selectedFolders.includes(id); });
    var collections = getUserCollectionEntities(state);
    var updateFolderSize = function (sizeChange, folderId) {
        if (!collections[folderId]) {
            return;
        }
        entities.collections[folderId] = __assign(__assign({}, collections[folderId]), { size: collections[folderId].size + sizeChange });
    };
    addedFolders.forEach(updateFolderSize.bind(1));
    removedFolders.forEach(updateFolderSize.bind(-1));
    return entities;
}
export function getNewFolderNameBlacklist(state) {
    var collections = getCollectionsRaw(state);
    return collections.map(function (collection) { return collection.name; });
}
export function getIsDeviationInitialized(state, deviationId) {
    var slice = getStoreSlice(state);
    return (slice.deviations.hasOwnProperty(deviationId) &&
        slice.deviations[deviationId].isInitializedForCollecting);
}
export function getSelectedFolderIds(state, deviationId) {
    var slice = getDeviationState(state, deviationId);
    return slice.selectedCollections;
}
export function getFeaturedId(state) {
    var collections = getCollectionsRaw(state);
    var featured = collections.find(function (f) { return f.name === 'Featured'; });
    return featured && featured.folderId;
}
export function getChangeDetails(state, deviationId) {
    var _a = getDeviationState(state, deviationId), selected = _a.selectedCollections, saved = _a.savedCollections;
    var added = selected.filter(function (f) { return !saved.includes(f); });
    var removed = saved.filter(function (f) { return !selected.includes(f); });
    var kept = saved.filter(function (f) { return selected.includes(f); });
    var wasFavourite = getIsFavourite(state, deviationId);
    var willBeFavourite = selected.length > 0;
    return {
        added: added,
        removed: removed,
        selected: selected,
        saved: saved,
        changeType: getChangeType(added.length, removed.length, kept.length),
        wasFavourite: wasFavourite,
        willBeFavourite: willBeFavourite,
    };
}
export function getFaveButtonStateWithDefaults(state, deviationId, defaults) {
    var slice = getStoreSlice(state).deviations[deviationId];
    return {
        isFavourite: slice ? slice.isFavourite : defaults.isFavourite || false,
        isLoading: slice ? slice.isLoading : false,
        faveCount: slice ? slice.faveCount : defaults.faveCount || 0,
    };
}
// TODO: should update entities, really
export function getDeviationPatchedWithStoreState(state, deviation) {
    var slice = getStoreSlice(state).deviations[deviation.deviationId];
    if (!slice) {
        return deviation;
    }
    return __assign(__assign({}, deviation), { isFavourited: slice.isFavourite, stats: deviation.stats && __assign(__assign({}, deviation.stats), { favourites: slice.faveCount }) });
}
export function getIsFavourite(state, deviationId) {
    var slice = getDeviationState(state, deviationId);
    return slice.isFavourite;
}
export function getFaveCount(state, deviationId) {
    var slice = getDeviationState(state, deviationId);
    return slice.faveCount;
}
export function getIsFaveLoading(state, deviationId) {
    var slice = getDeviationState(state, deviationId);
    return slice.isLoading;
}
export function getJustFaved(state, deviationId) {
    var slice = getDeviationState(state, deviationId);
    return slice.justFaved;
}
//# sourceMappingURL=selectors.js.map